import React, { useContext, useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Button, Progress, Table, Tag, Typography, Card, Space, Tooltip,
    message, Input, Select, Spin, Row, Col, Statistic, Modal, Dropdown, Badge
} from 'antd';
import {
    PlusOutlined, SyncOutlined, SearchOutlined, AreaChartOutlined,
    CheckCircleOutlined, FireOutlined, EditOutlined, MenuOutlined,
    ArrowRightOutlined, SettingOutlined, CopyOutlined, CaretDownOutlined, ReloadOutlined
} from '@ant-design/icons';
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Content } from 'antd/es/layout/layout';
import AppSider from '../../components/AppSider';
import { AuthContext } from '../../../../config/AuthContext';
import { GetConjuntsPildores } from '../../../../dao/jutge/GetConjuntsPildores';
import { GetPracticaIdImagen } from '../../../../dao/GetPracticaIdImagen';
import { PostActivitat } from '../../../../dao/jutge/PostActivitat';
import JutgeColors, { aulaColor as aulaColorFn } from '../../../../styles/JutgeColors';
import ConfiguracioModal from './ConfiguracioModal';
import ConfigurarPildoresConjuntModal from './ConfigurarPildoresConjuntModal';
import CopyConjuntModal from './CopyConjuntModal';
import PildoresIdEstadistiques from '../../pildoresId/content/PildoresIdEstadistiques';

const { Title, Text } = Typography;

// ── Image component (same as Activitats) ──────────────────────────────────────
const ConjuntImagen: React.FC<{ id: number }> = ({ id }) => {
    const [base64, setBase64] = useState<string | null>(null);

    useEffect(() => {
        let mounted = true;
        GetPracticaIdImagen(id + '')
            .then((res: any) => {
                if (mounted && res?.imagen) {
                    const cleaned = res.imagen.split(':').pop();
                    setBase64(cleaned);
                }
            })
            .catch(() => { if (mounted) setBase64(null); });
        return () => { mounted = false; };
    }, [id]);

    if (!base64) return <div style={{ width: 50, height: 50, background: '#eee', borderRadius: 12 }} />;
    return (
        <img
            src={`data:image/png;base64,${base64}`}
            alt="conjunt"
            style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}
        />
    );
};

// ── Drag & drop row ────────────────────────────────────────────────────────────
interface RowContextProps {
    setActivatorNodeRef?: (element: HTMLElement | null) => void;
    listeners?: any;
    attributes?: any;
}

const RowContext = React.createContext<RowContextProps>({});

interface DragRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
    'data-row-key': string;
}
const TableDragRow = (props: DragRowProps) => {
    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
        id: props['data-row-key'],
    });
    const style: React.CSSProperties = {
        ...props.style,
        transform: CSS.Translate.toString(transform),
        transition,
        ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
    };
    const contextValue = useMemo(
        () => ({ setActivatorNodeRef, listeners, attributes }),
        [setActivatorNodeRef, listeners, attributes]
    );

    return (
        <RowContext.Provider value={contextValue}>
            <tr {...props} ref={setNodeRef} style={style} />
        </RowContext.Provider>
    );
};

// ── Main component ─────────────────────────────────────────────────────────────
const Pildores: React.FC = () => {
    const { isLoggedIn, user, setBreadCrumb } = useContext(AuthContext);
    const navigate = useNavigate();
    const userId = user?.id || (user as any)?.idUsuari;
    const isAdmin = user && (user.tipus === '0' || user.tipus === '1');

    const [dades, setDades] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [selectedAula, setSelectedAula] = useState<string | number>('all');
    const [modalConfig, setModalConfig] = useState(false);
    const [modalEstadistiques, setModalEstadistiques] = useState(false);
    const [modalPildores, setModalPildores] = useState(false);
    const [modalCopy, setModalCopy] = useState(false);
    const [selected, setSelected] = useState<any>(null);

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 1 } }));

    // ── Available aula filter options ──────────────────────────────────────────
    const availableAulas = useMemo(() => {
        if (!Array.isArray(dades)) return [];
        const map = new Map<number | string, string>();
        dades.forEach((item: any) => {
            if (item.id_aula !== undefined && item.id_aula !== null) {
                if (!map.has(item.id_aula)) map.set(item.id_aula, item.aula || `Aula ${item.id_aula}`);
            }
        });
        return Array.from(map.entries()).map(([id, nom]) => ({ id, nom }));
    }, [dades]);

    useEffect(() => {
        if (availableAulas.length === 1) setSelectedAula(availableAulas[0].id);
    }, [availableAulas]);

    // ── Filtered list ──────────────────────────────────────────────────────────
    const filteredDades = useMemo(() => {
        if (!Array.isArray(dades)) return [];
        return dades.filter((item: any) => {
            // Un alumno NUNCA ve conjunts No visible (1), Revisió (4) ni Ocult (5):
            // solo Actiu (3) i Tancat (2). (Defensa en frontend además del backend.)
            if (!isAdmin && ![2, 3].includes(Number(item.estat))) return false;
            if (selectedAula !== 'all' && String(item.id_aula) !== String(selectedAula)) return false;
            if (searchText.trim()) {
                const q = searchText.toLowerCase();
                const matches = (item.nom && item.nom.toLowerCase().includes(q)) ||
                    (item.aula && item.aula.toLowerCase().includes(q)) ||
                    String(item.id).includes(q);
                if (!matches) return false;
            }
            return true;
        });
    }, [dades, selectedAula, searchText, isAdmin]);

    // ── Stats banner ───────────────────────────────────────────────────────────
    const stats = useMemo(() => {
        if (!Array.isArray(filteredDades) || filteredDades.length === 0) return { total: 0, active: 0, completed: 0, avgProgress: 0 };
        let active = 0, completed = 0, sumPct = 0;
        filteredDades.forEach((c: any) => {
            if (Number(c.estat) === 3) active++;
            const total = c.total_pildores || 0;
            const done = c.pildores_completades || 0;
            if (done >= (total || 1) && total > 0) completed++;
            sumPct += total > 0 ? (done / total) * 100 : 0;
        });
        return {
            total: filteredDades.length,
            active,
            completed,
            avgProgress: Math.round(sumPct / filteredDades.length)
        };
    }, [filteredDades]);

    // ── Data fetch ─────────────────────────────────────────────────────────────
    async function fetchData(refresh = true) {
        if (refresh) setLoading(true);
        const data = await GetConjuntsPildores(userId);
        setDades(data || []);
        setLoading(false);
    }

    // ── Canvi ràpid d'estat del conjunt (practica tipus=2) ─────────────────────
    const [updatingStatusId, setUpdatingStatusId] = useState<number | null>(null);
    async function handleQuickChangeEstat(record: any, newEstat: number) {
        if (updatingStatusId) return;
        setUpdatingStatusId(record.id);
        try {
            const payload = {
                id: record.id,
                nom: record.nom,
                estat: newEstat,
                id_aula: record.id_aula,
                is_examen: 0,
                is_concurs: 0,
                is_ide: 0,
                context: record.context || "",
                id_usuari: userId,
                tipus: 2, // Conjunt de Pildores
                id_season: record.id_season || 1,
                inici: record.inici || null,
                fin: record.final || null,
                javamons: record.javamons ?? 10,
                minim: record.minim ?? 0,
                escena: record.escena || null,
                inst: "",
                ras: []
            };
            await PostActivitat(payload);
            setDades(prev => (prev || []).map(c => c.id === record.id ? { ...c, estat: newEstat } : c));
            message.success("Estat del conjunt actualitzat");
            fetchData(false);
        } catch (err) {
            message.error("Error al canviar l'estat");
        } finally {
            setUpdatingStatusId(null);
        }
    }

    useEffect(() => {
        if (isLoggedIn) {
            setBreadCrumb([
                { nom: 'Home', nav: '/home' },
                { nom: 'Jutge', nav: '/jutge' },
                { nom: 'Pildores', nav: '/jutge/pildores' }
            ], true);
            fetchData();
        }
    }, [isLoggedIn]);

    // ── Drag reorder ───────────────────────────────────────────────────────────
    const onDragEnd = ({ active, over }: any) => {
        if (!over || active.id === over.id) return;
        setDades(prev => {
            const oldIndex = prev.findIndex(i => String(i.id) === active.id);
            const newIndex = prev.findIndex(i => String(i.id) === over.id);
            if (oldIndex === -1 || newIndex === -1) return prev;
            const moved = arrayMove(prev, oldIndex, newIndex);
            // Persist order
            const ordenList = moved.map((item, index) => ({ id: item.id, ordre: index + 1 }));
            fetch(`${process.env.REACT_APP_API_URL}:8061/jutge/pildores/ordre`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(ordenList)
            }).catch(() => message.error('Error guardando el orden'));
            return moved;
        });
    };

    // ── Column definition ──────────────────────────────────────────────────────
    const baseColumns: any[] = [
        {
            key: 'imagen',
            title: '',
            width: 70,
            render: (_: any, record: any) => <ConjuntImagen id={record.id} />,
        },
        {
            key: 'nom',
            title: 'Conjunt de Píldores',
            width: 150,
            render: (_: any, record: any) => {
                const pct = record.total_pildores > 0
                    ? Math.round((record.pildores_completades / record.total_pildores) * 100)
                    : 0;

                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <Text strong style={{ fontSize: '1rem' }}>
                            {record.nom}
                            <Text type="secondary" style={{ fontSize: '0.75rem', fontWeight: 400, marginLeft: 6 }}>ID: {record.id}</Text>
                        </Text>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            {record.id_aula ? (
                                <Tag color={aulaColorFn(record.id_aula)} style={{ fontSize: '0.75rem', marginRight: 0, color: '#fff', border: 'none' }}>
                                    {record.aula || `Aula ${record.id_aula}`}
                                </Tag>
                            ) : (
                                <Tag color="default" style={{ fontSize: '0.75rem', marginRight: 0 }}>Global</Tag>
                            )}
                            <Tag color="purple" style={{ fontSize: '0.75rem', marginRight: 0 }}>
                                💊 {record.total_pildores || 0} Píldores
                            </Tag>
                        </div>
                    </div>
                );
            }
        },
        {
            key: 'progres',
            title: 'Progrés',
            align: 'center' as const,
            width: 240,
            render: (_: any, record: any) => {
                const total = record.total_pildores || 0;
                const done = record.pildores_completades || 0;
                const inProgress = record.pildores_en_progres || 0;
                const pct = total > 0 ? Math.round((done / total) * 100) : 0;

                const strokeColor = pct >= 100 ? '#52c41a' : pct > 0 ? '#1890ff' : '#d9d9d9';
                return (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
                        <Progress
                            type="circle"
                            percent={pct}
                            size={48}
                            strokeColor={strokeColor}
                            format={p => <span style={{ fontSize: 12, fontWeight: 600 }}>{p}%</span>}
                        />
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
                            <Text style={{ fontSize: 13, fontWeight: 600 }}>
                                {done} <Text type="secondary" style={{ fontWeight: 400 }}>de</Text> {total}
                            </Text>
                            <Text type="secondary" style={{ fontSize: 11 }}>
                                {total === 1 ? 'píldora' : 'píldores'}
                            </Text>
                            {inProgress > done && (
                                <Text style={{ fontSize: 11, color: '#1890ff' }}>
                                    <SyncOutlined spin /> {inProgress - done} en curs
                                </Text>
                            )}
                        </div>
                    </div>
                );
            }
        },
        {
            key: 'estat',
            title: 'Estat',
            align: 'center' as const,
            width: 150,
            // El filtre d'estat només té sentit per a admin (l'alumne només veu Actiu/Tancat)
            ...(isAdmin ? {
                filters: [
                    { text: 'Actiu', value: 3 },
                    { text: 'Tancat', value: 2 },
                    { text: 'Revisió', value: 4 },
                    { text: 'No visible', value: 1 },
                    { text: 'Ocult', value: 5 },
                ],
                defaultFilteredValue: ['1', '2', '3', '4'],
                onFilter: (value: any, record: any) => Number(record.estat) === Number(value),
            } : {}),
            render: (_: any, record: any) => {
                const e = Number(record.estat);
                const label = e === 3 ? 'Actiu' : e === 2 ? 'Tancat' : e === 4 ? 'Revisió' : e === 5 ? 'Ocult' : 'No visible';
                const color = e === 3 ? 'green' : e === 2 ? 'orange' : e === 4 ? 'blue' : e === 5 ? 'default' : 'red';
                const tag = (
                    <Tag color={color} style={{ fontWeight: 600, cursor: isAdmin ? 'pointer' : 'default' }}>
                        {updatingStatusId === record.id ? <Spin size="small" style={{ marginRight: 4 }} /> : null}
                        {label}
                        {isAdmin && updatingStatusId !== record.id && <CaretDownOutlined style={{ fontSize: '0.65rem', opacity: 0.7, marginLeft: 4 }} />}
                    </Tag>
                );
                if (!isAdmin) return tag;
                return (
                    <Dropdown
                        trigger={['click']}
                        menu={{
                            items: [
                                { key: '3', label: <Tag color="green" style={{ fontWeight: 'bold' }}>Actiu</Tag>, onClick: () => handleQuickChangeEstat(record, 3) },
                                { key: '2', label: <Tag color="orange" style={{ fontWeight: 'bold' }}>Tancat</Tag>, onClick: () => handleQuickChangeEstat(record, 2) },
                                { key: '4', label: <Tag color="blue" style={{ fontWeight: 'bold' }}>Revisió</Tag>, onClick: () => handleQuickChangeEstat(record, 4) },
                                { key: '1', label: <Tag color="red" style={{ fontWeight: 'bold' }}>No visible</Tag>, onClick: () => handleQuickChangeEstat(record, 1) },
                                { key: '5', label: <Tag color="default" style={{ fontWeight: 'bold' }}>Ocult</Tag>, onClick: () => handleQuickChangeEstat(record, 5) },
                            ]
                        }}
                    >
                        <span onClick={e => e.stopPropagation()}>{tag}</span>
                    </Dropdown>
                );
            }
        },
        {
            key: 'actions',
            title: 'Accions',
            align: 'center' as const,
            width: 250,
            render: (_: any, record: any) => {
                // Acceso según estado + fechas (igual que Activitats). Admin siempre entra.
                const now = new Date();
                const iniciDate = record.inici ? new Date(record.inici.toString().substring(0, 19).replace('T', ' ')) : null;
                const finalDate = record.final ? new Date(record.final.toString().substring(0, 19).replace('T', ' ')) : null;
                const notYetOpen = !!(iniciDate && iniciDate > now);
                const pastDeadline = !!(finalDate && finalDate < now);
                const isActiu = Number(record.estat) === 3;
                const isTancat = Number(record.estat) === 2;
                // El alumno solo accede si está Actiu y dentro de plazo
                const canAccess = isAdmin || (isActiu && !notYetOpen && !pastDeadline);

                let accessBtn;
                if (canAccess) {
                    accessBtn = (
                        <Tooltip title="Accedir al conjunt de píldores">
                            <Button type="primary" shape="circle" icon={<ArrowRightOutlined />}
                                style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                                onClick={() => navigate(`/jutge/pildores/${record.id}`)} />
                        </Tooltip>
                    );
                } else if (notYetOpen) {
                    accessBtn = (
                        <Tooltip title={`Disponible a partir del ${iniciDate ? iniciDate.toLocaleString() : ''}`}>
                            <Tag color="default" style={{ fontWeight: 600 }}>🔒 Encara no disponible</Tag>
                        </Tooltip>
                    );
                } else if (isTancat || pastDeadline) {
                    accessBtn = (
                        <Tooltip title="Aquest conjunt està tancat">
                            <Tag color="orange" style={{ fontWeight: 600 }}>Tancat</Tag>
                        </Tooltip>
                    );
                } else {
                    accessBtn = <Tag color="default" style={{ fontWeight: 600 }}>No disponible</Tag>;
                }

                return (
                <Space onClick={e => e.stopPropagation()}>
                    {accessBtn}
                    {isAdmin && (
                        <>
                            <Tooltip title="Configurar píldores del conjunt">
                                <Button
                                    shape="circle"
                                    icon={<SettingOutlined />}
                                    style={{ backgroundColor: '#13c2c2', borderColor: '#13c2c2', color: '#fff' }}
                                    onClick={() => { setSelected(record); setModalPildores(true); }}
                                />
                            </Tooltip>
                            <Tooltip title="Copiar conjunt a altres aules">
                                <Button
                                    shape="circle"
                                    icon={<CopyOutlined />}
                                    style={{ backgroundColor: JutgeColors.colors.color4, borderColor: JutgeColors.colors.color4, color: '#fff' }}
                                    onClick={() => { setSelected(record); setModalCopy(true); }}
                                />
                            </Tooltip>
                            <Tooltip title="Editar conjunt">
                                <Button
                                    shape="circle"
                                    icon={<EditOutlined />}
                                    onClick={() => { setSelected(record); setModalConfig(true); }}
                                />
                            </Tooltip>
                            <Tooltip title="Estadístiques alumnes (Modal)">
                                <Button
                                    shape="circle"
                                    icon={<AreaChartOutlined />}
                                    style={{ backgroundColor: '#722ed1', border: 'none', color: '#fff' }}
                                    onClick={() => {
                                        setSelected(record);
                                        setModalEstadistiques(true);
                                    }}
                                />
                            </Tooltip>
                        </>
                    )}
                </Space>
                );
            }
        }
    ];

    const DragHandle: React.FC = () => {
        const { setActivatorNodeRef, listeners, attributes } = useContext(RowContext);
        return (
            <span
                ref={setActivatorNodeRef}
                {...listeners}
                {...attributes}
                style={{ cursor: 'grab', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '4px' }}
            >
                <MenuOutlined style={{ color: '#999', pointerEvents: 'none' }} />
            </span>
        );
    };

    const columns = isAdmin ? [
        {
            key: 'sort',
            align: 'center' as const,
            width: 30,
            render: () => <DragHandle />
        },
        ...baseColumns
    ] : baseColumns;

    // ── Render ─────────────────────────────────────────────────────────────────
    return (
        <>
            <AppSider currentVal={5} setPantalla={() => { }} />
            <Content style={{ background: '#f8f9fb', padding: 24, minHeight: '100vh' }}>
                {/* Capçalera */}
                <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <Title level={3} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <FireOutlined style={{ color: '#722ed1' }} />
                                Blocs de Píldores
                            </Title>
                            {Array.isArray(dades) && (
                                <Badge count={filteredDades.length} style={{ backgroundColor: '#722ed1', fontWeight: 600 }} overflowCount={999} />
                            )}
                        </div>
                        <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                            Gestiona els conjunts de píldores i el seguiment dels alumnes
                        </Typography.Text>
                    </div>

                    <Space wrap size="middle">
                        {availableAulas.length > 1 && (
                            <Select
                                value={selectedAula}
                                onChange={setSelectedAula}
                                style={{ minWidth: 160, borderRadius: 8 }}
                                options={[
                                    { label: '🏫 Totes les aules', value: 'all' },
                                    ...availableAulas.map(a => ({ label: `🏫 ${a.nom}`, value: a.id }))
                                ]}
                            />
                        )}
                        <Input
                            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                            placeholder="Cercar per nom, aula o ID..."
                            allowClear
                            value={searchText}
                            onChange={e => setSearchText(e.target.value)}
                            style={{ width: 220, borderRadius: 8 }}
                        />
                        {isAdmin && (
                            <Button type="primary" icon={<PlusOutlined />} style={{ borderRadius: 8 }}
                                onClick={() => { setSelected(null); setModalConfig(true); }}>
                                Nou Conjunt
                            </Button>
                        )}
                        <Button icon={<ReloadOutlined />} onClick={() => fetchData()} loading={loading} style={{ borderRadius: 8 }}>
                            Refrescar
                        </Button>
                    </Space>
                </div>

                {/* Targetes KPI */}
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                    <Col xs={24} sm={12} md={6}>
                        <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 10px rgba(0,0,0,0.04)' }} bodyStyle={{ padding: '16px 20px' }}>
                            <Statistic
                                title={<span style={{ color: '#8c8c8c', fontSize: 13 }}>Conjunts</span>}
                                value={stats.total}
                                prefix={<FireOutlined style={{ color: '#722ed1', marginRight: 6 }} />}
                                valueStyle={{ fontWeight: 700, fontSize: 26 }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 10px rgba(0,0,0,0.04)' }} bodyStyle={{ padding: '16px 20px' }}>
                            <Statistic
                                title={<span style={{ color: '#8c8c8c', fontSize: 13 }}>Actius</span>}
                                value={stats.active}
                                suffix={<span style={{ fontSize: 14, color: '#8c8c8c' }}>/ {stats.total}</span>}
                                prefix={<CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />}
                                valueStyle={{ color: '#52c41a', fontWeight: 700, fontSize: 26 }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 10px rgba(0,0,0,0.04)' }} bodyStyle={{ padding: '16px 20px' }}>
                            <Statistic
                                title={<span style={{ color: '#8c8c8c', fontSize: 13 }}>Completats</span>}
                                value={stats.completed}
                                suffix={<span style={{ fontSize: 14, color: '#8c8c8c' }}>/ {stats.total}</span>}
                                prefix={<FireOutlined style={{ color: '#fa8c16', marginRight: 6 }} />}
                                valueStyle={{ color: '#fa8c16', fontWeight: 700, fontSize: 26 }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 10px rgba(0,0,0,0.04)' }} bodyStyle={{ padding: '16px 20px' }}>
                            <Statistic
                                title={<span style={{ color: '#8c8c8c', fontSize: 13 }}>Progrés Mitjà</span>}
                                value={stats.avgProgress}
                                suffix="%"
                                prefix={<AreaChartOutlined style={{ color: stats.avgProgress >= 50 ? '#52c41a' : '#faad14', marginRight: 6 }} />}
                                valueStyle={{ color: stats.avgProgress >= 50 ? '#52c41a' : '#faad14', fontWeight: 700, fontSize: 26 }}
                            />
                        </Card>
                    </Col>
                </Row>

                {/* Taula principal */}
                <Card
                    bordered={false}
                    bodyStyle={{ padding: 0 }}
                    style={{ borderRadius: 14, overflow: 'hidden', boxShadow: '0 4px 16px rgba(0,0,0,0.05)', background: '#fff' }}
                >
                    <DndContext sensors={sensors} modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
                        <SortableContext
                            items={filteredDades.map(i => String(i.id))}
                            strategy={verticalListSortingStrategy}
                        >
                            <Table
                                components={isAdmin ? { body: { row: TableDragRow } } : undefined}
                                columns={columns}
                                dataSource={filteredDades}
                                rowKey={r => String(r.id)}
                                pagination={{ pageSize: 10 }}
                                size="middle"
                                bordered={false}
                                loading={loading}
                                style={{ borderRadius: 14, overflow: 'hidden', background: '#fff' }}
                            />
                        </SortableContext>
                    </DndContext>
                </Card>
            </Content>

            {/* Config Modal (create/edit conjunt) */}
            {modalConfig && (
                <ConfiguracioModal
                    visible={modalConfig}
                    onClose={() => { setModalConfig(false); fetchData(); }}
                    activitat={selected}
                    defaultTipus={2}
                />
            )}

            {/* Configurar Pildores per Conjunt */}
            {modalPildores && selected && (
                <ConfigurarPildoresConjuntModal
                    visible={modalPildores}
                    onClose={() => { setModalPildores(false); fetchData(); }}
                    conjunt={selected}
                    onUpdated={() => fetchData(false)}
                />
            )}

            {/* Copiar Conjunt a altres aules */}
            {modalCopy && selected && (
                <CopyConjuntModal
                    id_conjunt={selected.id}
                    nom={selected.nom}
                    visible={modalCopy}
                    onClose={(updated) => { setModalCopy(false); setSelected(null); if (updated) fetchData(); }}
                />
            )}

            {/* Modal Estadístiques Alumnes */}
            {modalEstadistiques && selected && (
                <Modal
                    title={`📊 Estadístiques Alumnes - ${selected.nom || 'Conjunt'}`}
                    open={modalEstadistiques}
                    onCancel={() => setModalEstadistiques(false)}
                    footer={null}
                    width="90vw"
                    style={{ top: 24, maxWidth: 1600 }}
                    bodyStyle={{ maxHeight: '82vh', overflowY: 'auto' }}
                    destroyOnClose
                >
                    <PildoresIdEstadistiques id_conjunt={selected.id} />
                </Modal>
            )}
        </>
    );
};

export default Pildores;
